import type { ScoreSnapshot } from "./ScoreSystem";

export interface RunMetadata {
  readonly runId: string;
  readonly startedAt: string;
  /** The generator seed the run was played from. */
  readonly seed: number;
}

export interface CompletedRunInput {
  readonly runId: string;
  readonly airportCode: "WEB";
  /**
   * The seed the run was played from.
   *
   * Nothing verifies it today — the board records it so that a replay, when
   * there is one, has something to replay. Adding the column later would
   * leave every row written until then permanently uncheckable.
   */
  readonly seed: number;
  /** What the board ranks on. */
  readonly score: number;
  readonly landings: number;
  readonly departures: number;
  readonly durationSeconds: number;
  readonly startedAt: string;
}

/**
 * An unguessable tail for the run id.
 *
 * The rest of the id is two readings of the wall clock, which anyone can
 * enumerate. The board's uniqueness rule turns a guessed id into a way to
 * squat on someone else's run — their genuine submission comes back as a
 * duplicate and is never recorded — so the id carries real randomness. Falls
 * back to `Math.random` only where `crypto` is absent, which is no worse than
 * what it replaces.
 */
function randomTail(): string {
  const source =
    typeof globalThis.crypto?.getRandomValues === "function"
      ? globalThis.crypto
      : null;
  if (!source) {
    return Math.random().toString(36).slice(2, 12).padEnd(10, "0");
  }
  const bytes = new Uint8Array(8);
  source.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    "",
  );
}

export function createRunMetadata(
  seed: number,
  startedAt = new Date(),
  tail = randomTail(),
): RunMetadata {
  const isoStartedAt = startedAt.toISOString();
  return Object.freeze({
    runId: `web-${startedAt.getTime().toString(36)}-${(seed >>> 0).toString(36)}-${tail}`,
    startedAt: isoStartedAt,
    seed,
  });
}

export function toCompletedRun(
  score: ScoreSnapshot,
  metadata: RunMetadata,
): CompletedRunInput {
  return Object.freeze({
    runId: metadata.runId,
    airportCode: "WEB",
    seed: metadata.seed,
    score: score.score,
    landings: score.landings,
    departures: score.departures,
    durationSeconds: score.durationSeconds,
    startedAt: metadata.startedAt,
  });
}
